import { useState, useCallback } from 'react';
import { Place, PlaceContext, DropPoint } from '@m4a3/api-client';

export interface SelectionState {
  // Place selection
  selectedPlace: Place | null;
  selectedPlaceContext: PlaceContext | null;
  
  // Drop point selection  
  selectedDropPoint: DropPoint | null;
  selectedDropPointId: string | null;
}

export const useSelectionState = () => {
  const [selectedPlace, setSelectedPlace] = useState<Place | null>(null);
  const [selectedPlaceContext, setSelectedPlaceContext] = useState<PlaceContext | null>(null);
  const [selectedDropPoint, setSelectedDropPoint] = useState<DropPoint | null>(null);
  const [selectedDropPointId, setSelectedDropPointId] = useState<string | null>(null);

  // Select a place and its context together
  const selectPlace = useCallback((place: Place, context: PlaceContext) => {
    setSelectedPlace(place);
    setSelectedPlaceContext(context);
    // Clear drop point selection when switching places
    setSelectedDropPoint(null);
    setSelectedDropPointId(null);
  }, []);

  // Select a drop point
  const selectDropPoint = useCallback((dropPoint: DropPoint | null) => {
    setSelectedDropPoint(dropPoint);
    setSelectedDropPointId(dropPoint?.id || null);
  }, []);

  // Update place context (for refreshing data)
  const updatePlaceContext = useCallback((context: PlaceContext) => {
    setSelectedPlaceContext(context);
  }, []);

  // Clear all selections
  const clearSelection = useCallback(() => {
    setSelectedPlace(null);
    setSelectedPlaceContext(null);
    setSelectedDropPoint(null);
    setSelectedDropPointId(null);
  }, []);

  // Clear only drop point selection
  const clearDropPointSelection = useCallback(() => {
    setSelectedDropPoint(null);
    setSelectedDropPointId(null);
  }, []);

  // Derived values for easier access
  const selectedPlaceId = selectedPlace?.id || null;
  const hasPlaceSelected = selectedPlace !== null;
  const hasDropPointSelected = selectedDropPoint !== null;

  return {
    // State
    selectedPlace,
    selectedPlaceContext,
    selectedDropPoint,
    selectedDropPointId,
    
    // Derived values
    selectedPlaceId,
    hasPlaceSelected,
    hasDropPointSelected,
    
    // Actions
    selectPlace,
    selectDropPoint,
    updatePlaceContext,
    clearSelection,
    clearDropPointSelection,
    
    // Direct setters (for advanced use cases)
    setSelectedPlace,
    setSelectedPlaceContext,
    setSelectedDropPoint,
    setSelectedDropPointId,
  };
};
